// src/airport/tabs/LiveTab.tsx — "Live" tab on AirportDetail.
//
// Shows three live-data subsections fetched from AeroAPI via our edge proxies:
//   1. Delays card (top)
//   2. Scheduled arrivals / departures toggle (default: arrivals)
//
// ── Strict offline policy ─────────────────────────────────────────────────
//
// Per the AeroAPI feature roadmap (data/aeroapi-feature-roadmap-2026-05-17.md):
//
//   > Airport delays / scheduled traffic → STRICT online-only.
//   > Cache the last response per airport in IDB but only show it if <1 hour
//   > old (this data goes stale fast). When offline or stale, hide the cards
//   > entirely or show a one-line "Live traffic unavailable offline"
//   > placeholder. Never show stale-and-pretending-to-be-fresh.
//
//  - navigator.onLine === false → render placeholder immediately, skip fetch.
//  - Fetch timeout / error → if cache < 1h, show with "as of HH:MM" badge.
//    If cache missing or ≥ 1h, show placeholder.
//  - Delays card specifically: when no fresh data available, always show
//    "Delays unavailable offline" — NEVER a green "no delays" pill.
//
// ── Refresh semantics ─────────────────────────────────────────────────────
//
//   - First open of the Live tab → auto-fetch once.
//   - Subsequent visits → render cache only (no auto-fetch) until:
//       a) User taps "Refresh", OR
//       b) Cache is ≥ 1h old (data hidden, manual refresh required).
//   - 5s AbortController ceiling on all fetches.

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  RefreshCw,
  Loader2,
  Clock,
  WifiOff,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  Plane,
  PlaneLanding,
  PlaneTakeoff,
} from '../../shell/Icon';
import { aeroapiGet, aeroapiSet } from '../../lib/aeroapiCache.js';
import { formatLocalTimeBare } from '../../lib/timeFormat.js';
import type { AirportDelaysResponse, AirportDelay } from '../../../api/flightaware/airport-delays.js';
import type { AirportArrivalsResponse, ArrivalFlight } from '../../../api/flightaware/airport-arrivals.js';
import type { AirportDeparturesResponse, DepartureFlight } from '../../../api/flightaware/airport-departures.js';

// ── Constants ──────────────────────────────────────────────────────────────

/** Data older than this is treated as expired — hide it, don't show stale. */
const TTL_HARD_MS = 60 * 60 * 1000; // 1 hour
const FETCH_TIMEOUT_MS = 5000;
const DEFAULT_LIMIT = 10;

// ── Helpers ────────────────────────────────────────────────────────────────

function isExpired(cachedAt: number): boolean {
  return Date.now() - cachedAt >= TTL_HARD_MS;
}

function formatHHMM(ts: number): string {
  return formatLocalTimeBare(new Date(ts));
}

function formatFlightTime(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '\u2014';
  return formatLocalTimeBare(d);
}

type TrafficMode = 'arrivals' | 'departures';

// ── Sub-components ─────────────────────────────────────────────────────────

function OfflinePlaceholder({ subtitle }: { subtitle?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 gap-1.5 text-center">
      <WifiOff size={20} className="text-cockpit-500" />
      <p className="text-sm text-cockpit-300">Live traffic unavailable offline</p>
      {subtitle && <p className="text-xs text-cockpit-500">{subtitle}</p>}
    </div>
  );
}

function StalePlaceholder({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
      <Clock size={20} className="text-cockpit-500" />
      <p className="text-sm text-cockpit-300">Live traffic data expired</p>
      <p className="text-xs text-cockpit-500">Data older than 1 hour is hidden to avoid stale info.</p>
      <button
        onClick={onRefresh}
        className="mt-1 flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300"
      >
        <RefreshCw size={11} />
        Refresh now
      </button>
    </div>
  );
}

function TrendIcon({ trend }: { trend: string | null }) {
  if (trend === 'increasing') return <TrendingUp size={12} className="text-red-400" />;
  if (trend === 'decreasing') return <TrendingDown size={12} className="text-green-400" />;
  return <Minus size={12} className="text-cockpit-400" />;
}

function DelaysCard({
  data,
  cachedAt,
  dataSource,
}: {
  data: AirportDelaysResponse | null;
  cachedAt: number | null;
  dataSource: 'fresh' | 'cached' | 'unavailable';
}) {
  if (dataSource === 'unavailable') {
    return (
      <div className="rounded-lg border border-cockpit-700 bg-cockpit-800/60 px-3 py-3">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={13} className="text-cockpit-500" />
          <span className="text-xs font-semibold tracking-widest text-cockpit-400">DELAYS</span>
        </div>
        <p className="text-xs text-cockpit-400">Delays unavailable offline</p>
      </div>
    );
  }

  const staleLabel = dataSource === 'cached' && cachedAt !== null
    ? `as of ${formatHHMM(cachedAt)}`
    : null;

  if (!data) return null;

  if (!data.hasDelays || data.delays.length === 0) {
    return (
      <div className="rounded-lg border border-cockpit-700 bg-cockpit-800/60 px-3 py-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <CheckCircle size={13} className="text-green-400" />
            <span className="text-xs font-semibold tracking-widest text-cockpit-400">DELAYS</span>
          </div>
          {staleLabel && (
            <span className="text-[10px] text-cockpit-500">{staleLabel}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 bg-green-900/40 border border-green-700/50 text-green-300 text-[11px] font-medium rounded-full px-2 py-0.5">
            <CheckCircle size={9} />
            No delays reported
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-red-700/40 bg-red-900/20 px-3 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={13} className="text-red-400" />
          <span className="text-xs font-semibold tracking-widest text-red-400">DELAYS</span>
        </div>
        {staleLabel && (
          <span className="text-[10px] text-cockpit-500">{staleLabel}</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {data.delays.map((d: AirportDelay, i: number) => (
          <div key={i} className="bg-red-900/30 rounded-md px-2.5 py-2 border border-red-700/30">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-red-200 capitalize">
                {d.category.replace(/_/g, ' ')}
              </span>
              <div className="flex items-center gap-1">
                <TrendIcon trend={d.trend} />
                <span className="text-[10px] text-cockpit-400 capitalize">{d.trend ?? ''}</span>
              </div>
            </div>
            <p className="text-[11px] text-cockpit-300 mt-0.5 capitalize">{d.reason}</p>
            {(d.minDelay != null || d.maxDelay != null) && (
              <p className="text-[10px] text-red-300 mt-0.5">
                {d.minDelay != null && d.maxDelay != null
                  ? `${d.minDelay}\u2013${d.maxDelay} min`
                  : d.minDelay != null
                    ? `\u2265${d.minDelay} min`
                    : `\u2264${d.maxDelay} min`}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ArrivalRow({ flight }: { flight: ArrivalFlight }) {
  const time = formatFlightTime(flight.estimatedOn ?? flight.scheduledOn);
  const isDelayed = flight.estimatedOn && flight.scheduledOn && flight.estimatedOn > flight.scheduledOn;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-cockpit-700 last:border-b-0">
      <div className="w-14 flex-shrink-0 text-center">
        <div className={`text-xs font-mono font-semibold leading-tight ${isDelayed ? 'text-amber-300' : 'text-cockpit-100'}`}>
          {time}
        </div>
        {isDelayed && (
          <div className="text-[9px] text-cockpit-500 line-through leading-tight">
            {formatFlightTime(flight.scheduledOn)}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-cockpit-50">{flight.ident || '\u2014'}</span>
          {flight.aircraftType && (
            <span className="text-[10px] font-mono bg-cockpit-700 text-cockpit-300 px-1.5 py-0.5 rounded flex-shrink-0">
              {flight.aircraftType}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-[11px] text-cockpit-400">{flight.origin.code || '\u2014'}</span>
          {flight.origin.name && (
            <span className="text-[10px] text-cockpit-500 truncate">{flight.origin.name}</span>
          )}
        </div>
      </div>
      {flight.status && (
        <span className={`text-[9px] font-semibold uppercase tracking-wide flex-shrink-0 ${
          flight.status.toLowerCase().includes('delay') ? 'text-amber-400' :
          flight.status.toLowerCase().includes('cancel') ? 'text-red-400' :
          flight.status.toLowerCase().includes('land') ? 'text-green-400' :
          'text-cockpit-400'
        }`}>
          {flight.status.length > 8 ? flight.status.slice(0, 8) : flight.status}
        </span>
      )}
    </div>
  );
}

function DepartureRow({ flight }: { flight: DepartureFlight }) {
  const time = formatFlightTime(flight.estimatedOff ?? flight.scheduledOff);
  const isDelayed = flight.estimatedOff && flight.scheduledOff && flight.estimatedOff > flight.scheduledOff;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-cockpit-700 last:border-b-0">
      <div className="w-14 flex-shrink-0 text-center">
        <div className={`text-xs font-mono font-semibold leading-tight ${isDelayed ? 'text-amber-300' : 'text-cockpit-100'}`}>
          {time}
        </div>
        {isDelayed && (
          <div className="text-[9px] text-cockpit-500 line-through leading-tight">
            {formatFlightTime(flight.scheduledOff)}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-cockpit-50">{flight.ident || '\u2014'}</span>
          {flight.aircraftType && (
            <span className="text-[10px] font-mono bg-cockpit-700 text-cockpit-300 px-1.5 py-0.5 rounded flex-shrink-0">
              {flight.aircraftType}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <ChevronRight size={10} className="text-cockpit-600 flex-shrink-0" />
          <span className="text-[11px] text-cockpit-400">{flight.destination.code || '\u2014'}</span>
          {flight.destination.name && (
            <span className="text-[10px] text-cockpit-500 truncate">{flight.destination.name}</span>
          )}
        </div>
      </div>
      {flight.status && (
        <span className={`text-[9px] font-semibold uppercase tracking-wide flex-shrink-0 ${
          flight.status.toLowerCase().includes('delay') ? 'text-amber-400' :
          flight.status.toLowerCase().includes('cancel') ? 'text-red-400' :
          flight.status.toLowerCase().includes('depart') ? 'text-green-400' :
          'text-cockpit-400'
        }`}>
          {flight.status.length > 8 ? flight.status.slice(0, 8) : flight.status}
        </span>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

interface Props {
  ident: string;
}

// Per-fetch result: either fresh data from network or a cached entry
type FetchStatus = 'idle' | 'loading' | 'done' | 'error';

interface LiveData {
  delays: AirportDelaysResponse | null;
  arrivals: AirportArrivalsResponse | null;
  departures: AirportDeparturesResponse | null;
  cachedAt: number | null;
  /** 'fresh' = just fetched; 'cached' = from IDB; 'unavailable' = offline/expired/error */
  source: 'fresh' | 'cached' | 'unavailable';
}

export function LiveTab({ ident }: Props) {
  const [status, setStatus] = useState<FetchStatus>('idle');
  const [data, setData] = useState<LiveData>({
    delays: null,
    arrivals: null,
    departures: null,
    cachedAt: null,
    source: 'unavailable',
  });
  const [trafficMode, setTrafficMode] = useState<TrafficMode>('arrivals');
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const didInitRef = useRef(false);

  // ── cache keys ──────────────────────────────────────────────────────────
  const keyDelays = `live-delays|${ident}`;
  const keyArrivals = `live-arrivals|${ident}`;
  const keyDepartures = `live-departures|${ident}`;

  // ── load from IDB ───────────────────────────────────────────────────────
  const loadFromCache = useCallback(async (): Promise<LiveData> => {
    const [cDelays, cArrivals, cDeps] = await Promise.all([
      aeroapiGet<AirportDelaysResponse>(keyDelays),
      aeroapiGet<AirportArrivalsResponse>(keyArrivals),
      aeroapiGet<AirportDeparturesResponse>(keyDepartures),
    ]);

    // Use the oldest non-null cachedAt as the timestamp (most conservative)
    const timestamps = [cDelays?.cachedAt, cArrivals?.cachedAt, cDeps?.cachedAt]
      .filter((t): t is number => t != null);
    const oldestTs = timestamps.length > 0 ? Math.min(...timestamps) : null;

    const hasAnything = cDelays || cArrivals || cDeps;
    if (!hasAnything || oldestTs === null) {
      return { delays: null, arrivals: null, departures: null, cachedAt: null, source: 'unavailable' };
    }

    if (isExpired(oldestTs)) {
      return { delays: null, arrivals: null, departures: null, cachedAt: oldestTs, source: 'unavailable' };
    }

    return {
      delays: cDelays?.data ?? null,
      arrivals: cArrivals?.data ?? null,
      departures: cDeps?.data ?? null,
      cachedAt: oldestTs,
      source: 'cached',
    };
  }, [keyDelays, keyArrivals, keyDepartures]);

  // ── network fetch ───────────────────────────────────────────────────────
  const doFetch = useCallback(async () => {
    // Fast path: offline — render cache only (or placeholder)
    if (!navigator.onLine) {
      const cached = await loadFromCache();
      setData(cached);
      setStatus('done');
      return;
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setStatus('loading');
    setError(null);

    // Render cached data immediately while network call is in flight
    const existing = await loadFromCache();
    if (existing.source !== 'unavailable') {
      setData(existing);
    }

    const timer = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

    try {
      const [rDelays, rArrivals, rDeps] = await Promise.all([
        fetch(`/api/flightaware/airport-delays?ident=${encodeURIComponent(ident)}`, { signal: ac.signal }),
        fetch(`/api/flightaware/airport-arrivals?ident=${encodeURIComponent(ident)}&limit=${DEFAULT_LIMIT}`, { signal: ac.signal }),
        fetch(`/api/flightaware/airport-departures?ident=${encodeURIComponent(ident)}&limit=${DEFAULT_LIMIT}`, { signal: ac.signal }),
      ]);
      clearTimeout(timer);

      if (rDelays.status === 503 || rArrivals.status === 503 || rDeps.status === 503) {
        // AeroAPI not configured — don't surface a scary error, just hide Live tab
        setData({ ...existing, source: 'unavailable' });
        setStatus('done');
        return;
      }

      if (!rDelays.ok || !rArrivals.ok || !rDeps.ok) {
        throw new Error(`HTTP ${[rDelays.status, rArrivals.status, rDeps.status].join('/')}`);
      }

      const [delays, arrivals, departures] = await Promise.all([
        rDelays.json() as Promise<AirportDelaysResponse>,
        rArrivals.json() as Promise<AirportArrivalsResponse>,
        rDeps.json() as Promise<AirportDeparturesResponse>,
      ]);

      const now = Date.now();
      await Promise.all([
        aeroapiSet(keyDelays, delays),
        aeroapiSet(keyArrivals, arrivals),
        aeroapiSet(keyDepartures, departures),
      ]);

      setData({ delays, arrivals, departures, cachedAt: now, source: 'fresh' });
      setStatus('done');
    } catch (e: unknown) {
      clearTimeout(timer);
      const isAbort = (e as Error)?.name === 'AbortError';
      // Fall back to cache if available
      const cached = await loadFromCache();
      if (cached.source !== 'unavailable') {
        setData(cached);
        setStatus('done');
      } else {
        setData({ delays: null, arrivals: null, departures: null, cachedAt: null, source: 'unavailable' });
        setStatus('error');
        if (!isAbort) setError((e as Error)?.message ?? 'Fetch failed');
      }
    }
  }, [ident, keyDelays, keyArrivals, keyDepartures, loadFromCache]);

  // ── init: auto-fetch on first open, skip if cache is fresh ─────────────
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    (async () => {
      // Check cache first
      const cached = await loadFromCache();
      if (cached.source !== 'unavailable') {
        // Cache is fresh — render it without a network call (boss policy)
        setData(cached);
        setStatus('done');
      } else {
        // No usable cache — auto-fetch once
        await doFetch();
      }
    })();

    return () => { abortRef.current?.abort(); };
  }, [ident, loadFromCache, doFetch]);

  // ── render ──────────────────────────────────────────────────────────────

  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
  const isLoading = status === 'loading';
  const noData = data.source === 'unavailable';
  const cachedAt = data.cachedAt;

  // Determine what to show for delays specifically
  // (never show green "no delays" pill when data is unavailable)
  const delaysSource: 'fresh' | 'cached' | 'unavailable' =
    noData ? 'unavailable' : data.source;

  return (
    <div className="px-3 py-3 flex flex-col gap-3">
      {/* Header: refresh button + timestamp */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {isLoading && <Loader2 size={13} className="text-cockpit-400 animate-spin flex-shrink-0" />}
          {!isLoading && cachedAt !== null && (
            <span className="flex items-center gap-1 text-[10px] text-cockpit-400">
              <Clock size={9} />
              Last refreshed {formatHHMM(cachedAt)}
              {data.source === 'cached' && (
                <span className="text-cockpit-500 ml-0.5">(cached)</span>
              )}
            </span>
          )}
          {isOffline && (
            <span className="flex items-center gap-1 text-[10px] text-amber-500">
              <WifiOff size={9} />
              Offline
            </span>
          )}
        </div>
        <button
          onClick={doFetch}
          disabled={isLoading}
          className={[
            'flex items-center gap-1 text-[11px] rounded-md px-2 py-1 flex-shrink-0',
            isLoading
              ? 'text-cockpit-500 bg-cockpit-700 cursor-not-allowed'
              : 'text-cockpit-200 bg-cockpit-700 hover:bg-cockpit-600',
          ].join(' ')}
          aria-label="Refresh live traffic"
        >
          {isLoading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          <span>Refresh</span>
        </button>
      </div>

      {/* Error banner (non-fatal) */}
      {error && !noData && (
        <div className="flex items-center gap-2 bg-amber-900/30 border border-amber-700/40 rounded-lg px-3 py-2 text-[11px] text-amber-200">
          <AlertTriangle size={11} className="flex-shrink-0" />
          <span>Refresh failed: {error}. Showing cached data.</span>
        </div>
      )}

      {/* ── Delays card ────────────────────────────────────────────────── */}
      {isOffline && !data.delays ? (
        <div className="rounded-lg border border-cockpit-700 bg-cockpit-800/60 px-3 py-3">
          <div className="flex items-center gap-2 mb-1">
            <WifiOff size={13} className="text-cockpit-500" />
            <span className="text-xs font-semibold tracking-widest text-cockpit-400">DELAYS</span>
          </div>
          <p className="text-xs text-cockpit-400">Delays unavailable offline</p>
        </div>
      ) : (
        <DelaysCard data={data.delays} cachedAt={cachedAt} dataSource={delaysSource} />
      )}

      {/* ── Traffic section ─────────────────────────────────────────────── */}
      {isOffline && noData ? (
        <OfflinePlaceholder subtitle="Live traffic requires internet." />
      ) : noData && !isLoading ? (
        status === 'error' ? (
          <StalePlaceholder onRefresh={doFetch} />
        ) : (
          <OfflinePlaceholder />
        )
      ) : !isLoading && !data.arrivals && !data.departures ? null : (
        <div className="rounded-lg border border-cockpit-700 overflow-hidden">
          {/* Mode toggle header */}
          <div className="flex border-b border-cockpit-700 bg-cockpit-800">
            <button
              onClick={() => setTrafficMode('arrivals')}
              className={[
                'flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors',
                trafficMode === 'arrivals'
                  ? 'text-sky-300 border-b-2 border-sky-500 bg-sky-900/20'
                  : 'text-cockpit-400 hover:text-cockpit-200 border-b-2 border-transparent',
              ].join(' ')}
              aria-pressed={trafficMode === 'arrivals'}
            >
              <PlaneLanding size={12} />
              Arrivals
            </button>
            <button
              onClick={() => setTrafficMode('departures')}
              className={[
                'flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors',
                trafficMode === 'departures'
                  ? 'text-sky-300 border-b-2 border-sky-500 bg-sky-900/20'
                  : 'text-cockpit-400 hover:text-cockpit-200 border-b-2 border-transparent',
              ].join(' ')}
              aria-pressed={trafficMode === 'departures'}
            >
              <PlaneTakeoff size={12} />
              Departures
            </button>
          </div>

          {/* Flight list */}
          {isLoading && !data.arrivals && !data.departures ? (
            <div className="flex items-center justify-center py-8 gap-2 text-cockpit-400">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Loading traffic&hellip;</span>
            </div>
          ) : trafficMode === 'arrivals' ? (
            data.arrivals && data.arrivals.flights.length > 0 ? (
              <>
                <div className="px-3 py-1.5 text-[10px] tracking-widest text-cockpit-400 bg-cockpit-800 border-b border-cockpit-700">
                  SCHEDULED ARRIVALS &middot; {data.arrivals.flights.length}
                  {data.source === 'cached' && cachedAt && (
                    <span className="ml-1 text-cockpit-500">&middot; as of {formatHHMM(cachedAt)}</span>
                  )}
                </div>
                {data.arrivals.flights.map((f, i) => (
                  <ArrivalRow key={`${f.ident}-${i}`} flight={f} />
                ))}
              </>
            ) : (
              <div className="py-6 text-center">
                <Plane size={18} className="text-cockpit-600 mx-auto mb-2" />
                <p className="text-sm text-cockpit-400">No scheduled arrivals</p>
              </div>
            )
          ) : (
            data.departures && data.departures.flights.length > 0 ? (
              <>
                <div className="px-3 py-1.5 text-[10px] tracking-widest text-cockpit-400 bg-cockpit-800 border-b border-cockpit-700">
                  SCHEDULED DEPARTURES &middot; {data.departures.flights.length}
                  {data.source === 'cached' && cachedAt && (
                    <span className="ml-1 text-cockpit-500">&middot; as of {formatHHMM(cachedAt)}</span>
                  )}
                </div>
                {data.departures.flights.map((f, i) => (
                  <DepartureRow key={`${f.ident}-${i}`} flight={f} />
                ))}
              </>
            ) : (
              <div className="py-6 text-center">
                <Plane size={18} className="text-cockpit-600 mx-auto mb-2" />
                <p className="text-sm text-cockpit-400">No scheduled departures</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
